import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback
} from "react";
import { SessionContextType } from "./types";
import { CrisisCooldownError } from "./errors";
import { useAuth } from "@/hooks/auth";
import { getSession as getSessionFromStore } from "@/store/sessions";
import { logger } from "@/utils";
import * as apiClient from "@/utils/serenity-api-client";
import { LiveKitTokenData } from "@/components/ChatSessionProvider";

export const SessionContext = createContext<SessionContextType | undefined>(
  undefined
);

/**
 * Props for the SessionProvider component
 */
interface SessionProviderProps {
  /** Child components that will have access to the session context */
  children: ReactNode;
}

/**
 * Session provider component
 *
 * Provides session state management to child components.
 *
 * @param props - Component props
 * @returns Provider component that supplies session context
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isArchived, setIsArchived] = useState<boolean>(false);
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(false);
  const [autoConnectForSessionId, setAutoConnectForSessionId] = useState<
    string | null
  >(null);
  const { user, getIdToken } = useAuth();

  // Session create / get only need a Firebase ID token. The AuthGate in
  // (drawer)/_layout.tsx already guarantees `user` is hydrated before any
  // protected screen mounts, so we don't re-check it here — `getIdToken()`
  // returning null is the truthful "not signed in" signal.
  const createSession = useCallback(
    async (patternName?: string): Promise<string> => {
      try {
        const token = await getIdToken();
        if (!token) {
          throw new Error("Not signed in");
        }

        // Call the server API to create a new session. A patternName seeds a
        // pattern-focused session (launched from a Pattern card).
        const result = await apiClient.createSession(token, patternName);
        if (!result.success) {
          if (result.crisisCooldown) {
            throw new CrisisCooldownError(result.crisisCooldown);
          }
          throw new Error(result.message);
        }

        const sessionId = result.sessionId;

        // Mark this session for auto-connect
        setAutoConnectForSessionId(sessionId);

        logger.info(`Created new session: ${sessionId}`);
        return sessionId;
      } catch (error) {
        logger.error("Failed to create session", error);
        throw error;
      }
    },
    [getIdToken]
  );

  const getSession = useCallback(
    async (sessionId: string): Promise<LiveKitTokenData> => {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Not signed in");
      }

      const result = await apiClient.getSession(sessionId, token);
      if (!result.success) {
        if (result.crisisCooldown) {
          throw new CrisisCooldownError(result.crisisCooldown);
        }
        throw new Error(result.message);
      }

      return result.payload as LiveKitTokenData;
    },
    [getIdToken]
  );

  const loadSessionStore = useCallback(
    async (sessionId: string): Promise<void> => {
      // Defensive: AuthGate guarantees user is set before any caller mounts.
      if (!user) {
        throw new Error("Not signed in");
      }

      try {
        setIsSessionLoading(true);
        setSessionId(sessionId); // Update context for other modules

        // Load session metadata to check if it's archived (legacy)
        const sessionData = await getSessionFromStore(user.uid, sessionId);
        if (sessionData) {
          const serverAssigned = sessionData.serverAssigned ?? false;
          setIsArchived(!serverAssigned); // Legacy sessions are archived
          logger.info(
            `Loaded session ${sessionId}, serverAssigned: ${serverAssigned}`
          );
        } else {
          logger.warn(`Session ${sessionId} not found`);
        }
      } catch (error) {
        logger.error("Failed to load session metadata", error);
        throw error;
      } finally {
        setIsSessionLoading(false);
      }
    },
    [user]
  );

  return (
    <SessionContext.Provider
      value={{
        sessionId,
        setSessionId,
        isArchived,
        setIsArchived,
        isSessionLoading,
        createSession,
        getSession,
        loadSessionStore,
        autoConnectForSessionId,
        setAutoConnectForSessionId
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Hook to access session context
 *
 * @returns SessionContextType containing sessionId and setSessionId
 * @throws Error if used outside of SessionProvider
 */
export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
